import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import {
  Building2, Briefcase, Users, Clock,
  CheckCircle, XCircle, TrendingUp, ArrowRight
} from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalCompanies: 0, pendingCompanies: 0,
    verifiedCompanies: 0, rejectedCompanies: 0,
    totalJobs: 0, totalApplications: 0,
    totalStudents: 0, hired: 0,
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [companiesRes, jobsRes, appsRes, studentsRes] =
        await Promise.all([
          supabase.from('companies').select('id, name, verification_status, created_at, contact_email, country, city').order('created_at', { ascending: false }),
          supabase.from('job_listings').select('id, is_active'),
          supabase.from('job_applications').select('id, status'),
          supabase.from('student_career_profiles').select('id'),
        ]);

      const cos = companiesRes.data || [];
      const jobs = jobsRes.data || [];
      const apps = appsRes.data || [];

      setStats({
        totalCompanies: cos.length,
        pendingCompanies: cos.filter(c => c.verification_status === 'pending').length,
        verifiedCompanies: cos.filter(c => c.verification_status === 'verified').length,
        rejectedCompanies: cos.filter(c => c.verification_status === 'rejected').length,
        totalJobs: jobs.filter(j => j.is_active).length,
        totalApplications: apps.length,
        totalStudents: studentsRes.data?.length || 0,
        hired: apps.filter(a => a.status === 'hired').length,
      });
      setRecentCompanies(cos.slice(0, 6));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: <Building2 size={22} />, label: 'Total Companies', value: stats.totalCompanies, color: '#4F8EF7', bg: 'rgba(79,142,247,0.1)' },
    { icon: <Clock size={22} />, label: 'Pending Review', value: stats.pendingCompanies, color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
    { icon: <CheckCircle size={22} />, label: 'Approved', value: stats.verifiedCompanies, color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
    { icon: <XCircle size={22} />, label: 'Rejected', value: stats.rejectedCompanies, color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
    { icon: <Briefcase size={22} />, label: 'Active Jobs', value: stats.totalJobs, color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
    { icon: <Users size={22} />, label: 'Applications', value: stats.totalApplications, color: '#22D3EE', bg: 'rgba(34,211,238,0.1)' },
    { icon: <Users size={22} />, label: 'Students Registered', value: stats.totalStudents, color: '#EC4899', bg: 'rgba(236,72,153,0.1)' },
    { icon: <TrendingUp size={22} />, label: 'Students Hired', value: stats.hired, color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
  ];

  const statusConfig = {
    pending: { color: '#F59E0B', bg: '#FEF3C7', label: 'Pending' },
    under_review: { color: '#3B82F6', bg: '#DBEAFE', label: 'Under Review' },
    verified: { color: '#10B981', bg: '#D1FAE5', label: 'Approved' },
    rejected: { color: '#EF4444', bg: '#FFE4E6', label: 'Rejected' },
  };

  return (
    <AdminLayout>
      {loading ? (
        <div style={{
          display: 'flex', justifyContent: 'center',
          alignItems: 'center', minHeight: 300,
        }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{
              fontSize: 24, fontWeight: 800, color: '#1E293B',
            }}>Admin Dashboard</h1>
            <p style={{ color: '#94A3B8', marginTop: 4, fontSize: 14 }}>
              UNIVERSE Careers · Platform Overview
            </p>
          </div>

          {/* Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14, marginBottom: 28,
          }}>
            {statCards.map(s => (
              <div key={s.label} style={{
                background: '#fff', borderRadius: 14,
                padding: '18px 20px',
                border: '1px solid #E2E8F0',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: 12,
                  background: s.bg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: s.color, flexShrink: 0,
                }}>{s.icon}</div>
                <div>
                  <div style={{
                    fontSize: 26, fontWeight: 800,
                    color: '#1E293B', lineHeight: 1,
                  }}>{s.value}</div>
                  <div style={{
                    fontSize: 11, color: '#94A3B8', marginTop: 4,
                  }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Pending alert */}
          {stats.pendingCompanies > 0 && (
            <div style={{
              background: 'linear-gradient(135deg,#FFF7ED,#FFFBEB)',
              border: '1.5px solid #FED7AA',
              borderRadius: 14, padding: '16px 20px',
              marginBottom: 24,
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 24 }}>⚠️</span>
                <div>
                  <div style={{
                    fontWeight: 700, color: '#92400E', fontSize: 14,
                  }}>
                    {stats.pendingCompanies} company application{stats.pendingCompanies > 1 ? 's' : ''} awaiting review
                  </div>
                  <div style={{ fontSize: 12, color: '#B45309' }}>
                    Review and approve/reject to allow companies to post jobs
                  </div>
                </div>
              </div>
              <Link to="/admin/companies" style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 16px',
                background: 'linear-gradient(135deg,#F59E0B,#D97706)',
                borderRadius: 10, color: '#fff',
                fontWeight: 700, fontSize: 13,
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
              }}>
                Review Now <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* Recent Companies */}
          <div style={{
            background: '#fff', borderRadius: 16,
            border: '1px solid #E2E8F0', overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center',
              padding: '18px 24px',
              borderBottom: '1px solid #F1F5F9',
            }}>
              <div style={{
                fontWeight: 700, color: '#1E293B', fontSize: 15,
              }}>Recent Company Applications</div>
              <Link to="/admin/companies" style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 12, color: '#4F8EF7', fontWeight: 600,
              }}>
                View all <ArrowRight size={13} />
              </Link>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFF' }}>
                  {['Company', 'Location', 'Status', 'Applied'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700,
                      color: '#94A3B8', letterSpacing: 0.5,
                    }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentCompanies.map((c, i) => {
                  const sc = statusConfig[c.verification_status] ||
                    statusConfig.pending;
                  return (
                    <tr key={c.id} style={{
                      borderTop: '1px solid #F1F5F9',
                      transition: 'background 0.1s',
                    }}
                      onMouseEnter={e =>
                        e.currentTarget.style.background = '#F8FAFF'}
                      onMouseLeave={e =>
                        e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{
                          fontWeight: 600, fontSize: 13, color: '#1E293B',
                        }}>{c.name}</div>
                        <div style={{
                          fontSize: 11, color: '#94A3B8',
                        }}>{c.contact_email}</div>
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        fontSize: 13, color: '#64748B',
                      }}>
                        {c.city}, {c.country}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          padding: '4px 10px', borderRadius: 6,
                          background: sc.bg, color: sc.color,
                        }}>{sc.label}</span>
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        fontSize: 12, color: '#94A3B8',
                      }}>
                        {new Date(c.created_at)
                          .toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric',
                          })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}